import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import { moveAnnotationsToBack } from "../lib/apexcharts_annotations"
import { computeAccountStats, computeCompetitorAverage, difference, formatFixed } from "../lib/competitor_summary_stats"

// 指標チェックボックス(metricCheckbox ターゲット)の並び順に対応するデータのキー
const METRIC_KEYS = ['follower', 'impression', 'avg_engagement', 'avg_engagement_rate']

export default class extends Controller {
  static targets = ["chart", "metricCheckbox", "accountCheckbox", "mainTitle", "mainStats", "compTitle", "compStats", "compTable", "compRow"]
  static values = {
    chartData: Object
  }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  initializeChart() {
    if (!this.chartDataValue || !ApexCharts) return

    this.chartData = this.chartDataValue
    this.renderChart()
    this.updateSummaryStats()
  }

  generateChartSeries() {
    const series = []
    const checkedMetrics = this.getCheckedMetrics()
    const metricLabels = this.getMetricLabels()

    const mainCheckbox = document.getElementById('checkbox-benchmark-0')
    if (mainCheckbox && mainCheckbox.checked) {
      checkedMetrics.forEach(metricKey => {
        const data = this.chartData.accounts.main.data[metricKey]
        series.push({
          name: `@${this.chartData.accounts.main.name} (${metricLabels[metricKey]})`,
          data: data,
          type: metricKey === 'avg_engagement' ? 'column' : 'line'
        })
      })
    }

    this.chartData.accounts.competitors.forEach((competitor, index) => {
      const checkbox = document.getElementById(`checkbox-benchmark-${index + 1}`)
      if (checkbox && checkbox.checked) {
        checkedMetrics.forEach(metricKey => {
          const data = competitor.data[metricKey]
          series.push({
            name: `@${competitor.name} (${metricLabels[metricKey]})`,
            data: data,
            type: metricKey === 'avg_engagement' ? 'column' : 'line'
          })
        })
      }
    })

    return series
  }

  getCheckedMetrics() {
    const checkedMetrics = []

    this.metricCheckboxTargets.forEach((checkbox, index) => {
      if (checkbox.checked) {
        checkedMetrics.push(METRIC_KEYS[index])
      }
    })

    return checkedMetrics
  }

  // 凡例の指標名は、ビューのチェックボックスの表示名(<label> の文言)を正とする。
  // 同じキーでも SNS ごとに意味が違う(impression は X で「インプレッション数」、Instagram で「いいね数」)ため、
  // JS に固定の対応表を持つと片方の SNS で凡例が誤る(Issue #1303)。
  // checkbox.labels はチェックボックスに紐付いた <label> で、スクリーンリーダーが読み上げる名前と同じもの
  getMetricLabels() {
    const labels = {}

    this.metricCheckboxTargets.forEach((checkbox, index) => {
      const key = METRIC_KEYS[index]
      labels[key] = checkbox.labels?.[0]?.textContent.trim() || key
    })

    return labels
  }

  renderChart() {
    if (!ApexCharts) return

    const series = this.generateChartSeries()

    const annotations = {
      xaxis: this.chartData.weekends.map(weekend => ({
        x: weekend.from,
        x2: weekend.to,
        fillColor: weekend.color,
        opacity: 0.4
      }))
    }

    const options = {
      chart: {
        type: 'line',
        height: 400,
        id: 'competitorChart',
        fontFamily: 'inherit',
        events: {
          mounted: () => moveAnnotationsToBack(this.chartTarget),
          updated: () => moveAnnotationsToBack(this.chartTarget)
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            zoom: true,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: true
          }
        }
      },
      series: series,
      xaxis: {
        type: 'datetime',
        timezone: 'Asia/Tokyo',
        min: Date.parse(this.chartData.fromDate),
        max: Date.parse(this.chartData.toDate),
        labels: {
          show: true,
          formatter: function(value) {
            const date = new Date(value)
            return isNaN(date.getTime()) ? value : `${date.getMonth() + 1}/${date.getDate()}`
          },
          datetimeUTC: false,
          style: {
            colors: '#374151',
            fontSize: '12px',
            fontFamily: 'inherit'
          }
        },
        axisBorder: { show: true, color: '#E5E7EB' },
        axisTicks: { show: true, color: '#E5E7EB' }
      },
      yaxis: {
        show: true,
        labels: {
          show: true,
          style: {
            colors: '#374151',
            fontSize: '12px',
            fontFamily: 'inherit'
          },
          formatter: function(value) {
            return new Intl.NumberFormat('ja-JP', {
              notation: 'compact',
              compactDisplay: 'short'
            }).format(value)
          }
        }
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: "bottom",
        horizontalAlign: "center",
        floating: false,
      },
      colors: ['#167bff', '#f31260', '#0bbf58', '#f59e0b', '#8b5cf6', '#06b6d4'],
      tooltip: {
        theme: 'light',
        style: { fontSize: '12px', fontFamily: 'inherit' },
        shared: true,
        intersect: false,
        x: {
          formatter: function(value) {
            const date = new Date(value)
            if (isNaN(date.getTime())) return value
            return `${date.getMonth() + 1}/${date.getDate()}`
          }
        },
        y: {
          formatter: function(value) {
            return new Intl.NumberFormat('ja-JP').format(value)
          }
        }
      },
      dataLabels: { enabled: false },
      stroke: { width: 2 },
      grid: { borderColor: '#E5E7EB', strokeDashArray: 3 },
      title: { text: '', show: false },
      annotations: annotations
    }

    if (this.chart) {
      this.chart.destroy()
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  updateChart() {
    const series = this.generateChartSeries()

    if (this.chart) {
      this.chart.updateSeries(series)
    } else {
      this.renderChart()
    }
  }

  // --- サマリー更新ロジック ---

  updateSummaryStats() {
    if (!this.chartData) return

    const mainChecked = document.getElementById('checkbox-benchmark-0')?.checked
    const checkedCompetitors = this.getCheckedCompetitors()

    // メインアカウントの統計(平均エンゲージメント率などはサーバー集計の stats を使う。#1624)
    const mainStats = computeAccountStats(this.chartData.accounts.main)

    // メインセクションの表示/非表示
    if (this.hasMainStatsTarget) {
      this.mainStatsTarget.style.display = mainChecked ? '' : 'none'
      this.mainTitleTarget.style.display = mainChecked ? '' : 'none'
      if (mainChecked) {
        this.setStat('main-followers', this.formatNumber(mainStats.followers))
        this.setStat('main-likes', this.formatNumber(mainStats.maxLikes))
        this.setStat('main-posts', formatFixed(mainStats.avgPosts, 1))
        this.setStat('main-engagement', formatFixed(mainStats.avgEngagement, 2, '%'))
      }
    }

    // 競合セクション
    if (this.hasCompStatsTarget) {
      const hasCompetitors = checkedCompetitors.length > 0
      this.compStatsTarget.style.display = hasCompetitors ? '' : 'none'
      this.compTitleTarget.style.display = hasCompetitors ? '' : 'none'
      if (this.hasCompTableTarget) {
        this.compTableTarget.closest('.card').style.display = hasCompetitors ? '' : 'none'
      }

      if (hasCompetitors) {
        // 競合平均を計算(対象投稿が無く null のアカウントは分母から除く)
        const compAvg = computeCompetitorAverage(checkedCompetitors.map(comp => computeAccountStats(comp)))
        const count = checkedCompetitors.length

        // タイトル更新
        this.compTitleTarget.textContent = `競合アカウント平均（${count}件）`

        // 値更新
        this.setStat('comp-followers', this.formatNumber(compAvg.followers))
        this.setStat('comp-likes', this.formatNumber(compAvg.maxLikes))
        this.setStat('comp-posts', formatFixed(compAvg.avgPosts, 1))
        this.setStat('comp-engagement', formatFixed(compAvg.avgEngagement, 2, '%'))

        // 差分更新
        if (mainChecked) {
          this.setDiff('diff-followers', mainStats.followers - compAvg.followers, 'number')
          this.setDiff('diff-likes', mainStats.maxLikes - compAvg.maxLikes, 'number')
          this.setDiff('diff-posts', difference(mainStats.avgPosts, compAvg.avgPosts), 'decimal')
          this.setDiff('diff-engagement', difference(mainStats.avgEngagement, compAvg.avgEngagement), 'percent')
        } else {
          // メインが非表示なら差分も非表示
          this.element.querySelectorAll('[data-stat^="diff-"]').forEach(el => el.style.display = 'none')
        }
      }
    }

    // テーブル行の表示/非表示
    this.compRowTargets.forEach(row => {
      const index = parseInt(row.dataset.competitorIndex)
      const checkbox = document.getElementById(`checkbox-benchmark-${index}`)
      row.style.display = checkbox?.checked ? '' : 'none'
    })
  }

  getCheckedCompetitors() {
    return this.chartData.accounts.competitors.filter((_, index) => {
      const checkbox = document.getElementById(`checkbox-benchmark-${index + 1}`)
      return checkbox?.checked
    })
  }

  setStat(key, value) {
    const el = this.element.querySelector(`[data-stat="${key}"]`)
    if (el) el.textContent = value
  }

  setDiff(key, diff, type) {
    const el = this.element.querySelector(`[data-stat="${key}"]`)
    if (!el) return

    // どちらかの値が無い(対象投稿なし)ときは差分を出さない
    if (diff == null) {
      el.style.display = 'none'
      return
    }

    el.style.display = ''
    el.classList.remove('text-success', 'text-error')
    el.classList.add(diff >= 0 ? 'text-success' : 'text-error')

    const sign = diff >= 0 ? '+' : ''
    if (type === 'number') {
      el.textContent = `自社との差${sign}${this.formatNumber(diff)}`
    } else if (type === 'decimal') {
      el.textContent = `自社との差${sign}${diff.toFixed(1)}`
    } else if (type === 'percent') {
      el.textContent = `自社との差${sign}${diff.toFixed(2)}%`
    }
  }

  formatNumber(num) {
    return new Intl.NumberFormat('ja-JP').format(Math.round(num))
  }

  // Stimulus action methods
  metricChanged(event) {
    // 最後の1指標は外せない。今外したチェックボックスを戻し、チャートには触らない
    // (チャートは既にその指標を描いているので、チェック状態と凡例が操作前と完全に一致したまま)。
    //
    // 以前は先頭(フォロワー数)を代わりにチェックして updateChart していたが、
    // ApexCharts 6.10 の updateSeries は「系列数も Y 軸スケールも変わらず名前だけ変わる」更新では
    // 内部の seriesNames だけ更新して凡例を描き直さないため、凡例が直前の指標名のまま残っていた(Issue #1304)。
    // 通常のチェック操作は1回ごとに系列数が変わるのでこの条件に当たらず、当たるのはこの経路だけだった
    if (this.getCheckedMetrics().length === 0) {
      event.target.checked = true
      return
    }

    this.updateChart()
  }

  accountChanged(event) {
    this.updateChart()
    this.updateSummaryStats()
  }

  // Calendar functionality
  applyDateRange() {
    const calendarElement = document.getElementById('range-cally-element')
    const value = calendarElement.value
    if (value) {
      const [fromDate, toDate] = value.split('/')
      const url = new URL(window.location)
      url.searchParams.set('fromDate', fromDate)
      url.searchParams.set('toDate', toDate)
      window.location.href = url.toString()
    }
  }

  closeCalendar() {
    document.querySelector('.dropdown-content').blur()
  }

  // Export functionality
  exportChart() {
    if (this.chart) {
      this.chart.dataURI().then(({ imgURI }) => {
        const link = document.createElement('a')
        const checkedMetrics = this.getCheckedMetrics()
        const metricsString = checkedMetrics.join('-') || 'all'
        link.download = `competitor-analysis-${metricsString}-${new Date().toISOString().split('T')[0]}.png`
        link.href = imgURI
        link.click()
      })
    }
  }
}
